import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../constants';

interface RecentRepair {
  makeName: string;
  modelName: string;
  customerName: string;
  customerMobile: string;
  customerArea: string;
  serviceSummary: string;
  orderDate: string;
  modelImageUrl?: string;
}

interface Props {
  onRequestFeedback: (mobile: string) => void;
}

const RecentRepairsCarousel: React.FC<Props> = ({ onRequestFeedback }) => {
  const [repairs, setRepairs] = useState<RecentRepair[]>([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/Order/recent-repairs?days=30&limit=20`)
      .then(r => r.json())
      .then(data => {
        if (data.isSuccess && data.entity?.length) {
          setRepairs(data.entity);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || repairs.length === 0) return null;

  const items = [...repairs, ...repairs];

  return (
    <div className="mb-6">
      <h2 className="text-base font-bold text-gray-800 mb-3 px-1 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse" />
        Latest Repairs Done
      </h2>

      <div className="overflow-hidden relative">
        <div className="absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        <div
          ref={trackRef}
          className="flex gap-3"
          style={{
            animation: `scroll-left ${repairs.length * 4}s linear infinite`,
            width: 'max-content',
          }}
        >
          {items.map((repair, i) => (
            <RepairCard
              key={i}
              repair={repair}
              onRequestFeedback={onRequestFeedback}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes scroll-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

interface CardProps {
  repair: RecentRepair;
  onRequestFeedback: (mobile: string) => void;
}

const RepairCard: React.FC<CardProps> = ({ repair, onRequestFeedback }) => {
  const firstName = repair.customerName?.split(' ')[0] ?? 'Customer';

  return (
    <div className="flex-shrink-0 w-52 bg-white rounded-2xl border border-gray-100 shadow-sm p-3.5 hover:shadow-md transition-shadow">
      {/* Device */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-100">
          {repair.modelImageUrl ? (
            <img
              src={repair.modelImageUrl}
              alt={repair.modelName}
              className="w-full h-full object-contain p-0.5"
            />
          ) : (
            <i className="fas fa-mobile-alt text-minto-600 text-xs" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-xs font-bold text-gray-800 truncate">{repair.modelName}</div>
          <div className="text-xs text-gray-400 truncate">{repair.makeName}</div>
        </div>
      </div>

      {/* Service */}
      {repair.serviceSummary && (
        <div className="text-xs text-minto-600 font-medium mb-2 truncate">
          🔧 {repair.serviceSummary}
        </div>
      )}

      {/* Customer */}
      <div className="border-t border-gray-50 pt-2 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400 text-xs">👤</span>
          <span className="text-xs text-gray-700 font-medium truncate">{firstName}</span>
        </div>
        {repair.customerArea && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400 text-xs">📍</span>
            <span className="text-xs text-gray-500 truncate">{repair.customerArea}</span>
          </div>
        )}
      </div>

      {/* Action */}
      <div className="mt-2.5">
        <button
          onClick={() => onRequestFeedback(repair.customerMobile)}
          className="w-full text-xs font-semibold text-minto-600 border border-minto-200 rounded-lg py-1.5 hover:bg-minto-50 transition-colors"
        >
          💬 Ask for Feedback
        </button>
      </div>

      {repair.orderDate && (
        <div className="text-xs text-gray-300 text-right mt-1">{repair.orderDate}</div>
      )}
    </div>
  );
};

export default RecentRepairsCarousel;
